import React, { useEffect, useState } from 'react';
import { Plane, WifiOff, Smartphone, Sparkles, ChevronRight, ChevronLeft, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useClientAuth } from '../context/ClientAuthContext';

const LS_KEY = 'demarre_onboarding_seen_v1';

const t = (lang, pt, en, es) => (lang === 'pt' ? pt : lang === 'en' ? en : es);

// Iter 320an (Feb 2026) — Renders label in all 3 languages stacked. Used
// for the onboarding modal so a client who mistakenly saw the app in the
// wrong language still understands the welcome message.
const TriLingualHeading = ({ pt, en, es, className = '' }) => (
  <div className={className}>
    <div className="font-bold leading-tight">{pt}</div>
    <div className="font-semibold text-white/85 text-[15px] leading-tight mt-1">{en}</div>
    <div className="font-medium text-white/70 text-[14px] leading-tight italic mt-0.5">{es}</div>
  </div>
);

const TriLingualBody = ({ pt, en, es }) => (
  <div className="space-y-2">
    <p className="text-sm text-gray-800 leading-relaxed">{pt}</p>
    <p className="text-[13px] text-gray-600 leading-relaxed">{en}</p>
    <p className="text-[12px] text-gray-500 leading-relaxed italic">{es}</p>
  </div>
);

/**
 * First-access onboarding modal for client PWA.
 * Shows once per device/browser unless reopened from profile.
 *
 * Props:
 *  - forceOpen: if true, opens regardless of LS flag (for "Ver tutorial" reopens)
 *  - onClose: called when user closes the modal
 */
const ClientOnboarding = ({ forceOpen = false, onClose }) => {
  const { language } = useLanguage();
  const { client } = useClientAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  // Feb 2026 (iter 260): The LanguagePreferenceModal must win when a client
  // has no preferred_language saved (blocking modal). Defer the onboarding
  // until after the language is picked, then let it show on the next visit.
  const languageNotChosen = !!client && !['pt', 'en', 'es'].includes(
    (client.preferred_language || '').toLowerCase()
  );

  useEffect(() => {
    if (forceOpen) {
      setOpen(true);
      setStep(0);
      return;
    }
    if (languageNotChosen) {
      // Stand down while LanguagePreferenceModal is active
      setOpen(false);
      return;
    }
    try {
      const seen = localStorage.getItem(LS_KEY);
      if (!seen) {
        // Slight delay so app finishes mounting first
        const timer = setTimeout(() => setOpen(true), 600);
        return () => clearTimeout(timer);
      }
    } catch { /* ignore */ }
  }, [forceOpen, languageNotChosen]);

  const close = () => {
    try { localStorage.setItem(LS_KEY, '1'); } catch { /* ignore */ }
    setOpen(false);
    if (onClose) onClose();
  };

  const slides = [
    {
      icon: Sparkles,
      title: { pt: 'Bem-vindo à Demarré Tours', en: 'Welcome to Demarré Tours', es: 'Bienvenido a Demarré Tours' },
      body: {
        pt: 'Sua jornada espiritual começa aqui. Vamos te mostrar rapidamente o que você pode fazer no aplicativo.',
        en: 'Your spiritual journey begins here. Let us quickly show you what you can do in the app.',
        es: 'Tu viaje espiritual comienza aquí. Te mostraremos rápidamente lo que puedes hacer en la aplicación.',
      },
      bg: 'from-demarre-800 to-demarre-900',
    },
    {
      icon: Plane,
      title: { pt: 'Suas peregrinações em um só lugar', en: 'All your pilgrimages in one place', es: 'Todas tus peregrinaciones en un solo lugar' },
      body: {
        pt: 'Acompanhe reservas, itinerários, hotéis, recibos e documentos em "Minhas Viagens".',
        en: 'Track bookings, itineraries, hotels, receipts and documents under "My Trips".',
        es: 'Sigue reservas, itinerarios, hoteles, recibos y documentos en "Mis Viajes".',
      },
      bg: 'from-emerald-700 to-emerald-900',
    },
    {
      icon: WifiOff,
      title: { pt: 'Funciona offline ✈️', en: 'Works offline ✈️', es: 'Funciona sin conexión ✈️' },
      body: {
        pt: 'No avião ou em locais sem internet, seus itinerários e áudios baixados continuam acessíveis. Toque "Baixar para offline" nos guias de áudio.',
        en: 'On a plane or in places without internet, your itineraries and downloaded audio remain accessible. Tap "Download for offline" on audio guides.',
        es: 'En el avión o en lugares sin internet, tus itinerarios y audios descargados siguen accesibles. Toca "Descargar para offline" en las audioguías.',
      },
      bg: 'from-sky-700 to-sky-900',
    },
    {
      icon: Smartphone,
      title: { pt: 'Instale como aplicativo', en: 'Install as an app', es: 'Instalar como aplicación' },
      body: {
        pt: 'No iPhone: toque o botão Compartilhar e escolha "Adicionar à Tela de Início". No Android: menu → "Instalar aplicativo". Acesso rápido como qualquer app.',
        en: 'On iPhone: tap Share and choose "Add to Home Screen". On Android: menu → "Install app". Quick access like any other app.',
        es: 'En iPhone: pulsa Compartir y elige "Agregar a pantalla de inicio". En Android: menú → "Instalar aplicación". Acceso rápido como cualquier otra app.',
      },
      bg: 'from-amber-700 to-amber-900',
    },
  ];

  if (!open) return null;

  const isLast = step === slides.length - 1;
  const Slide = slides[step];
  const Icon = Slide.icon;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-200"
      data-testid="client-onboarding"
      role="dialog"
      aria-modal="true"
      aria-labelledby="onboarding-title"
    >
      <div className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden">
        {/* Close button */}
        <button
          onClick={close}
          className="absolute top-3 right-3 z-10 p-2 rounded-full bg-white/80 hover:bg-white text-gray-700 shadow"
          aria-label={t(language, 'Fechar', 'Close', 'Cerrar')}
          data-testid="onboarding-close"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Visual header */}
        <div className={`bg-gradient-to-br ${Slide.bg} text-white px-6 py-8 relative`}>
          <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-white/15 backdrop-blur mb-4 mx-auto">
            <Icon className="w-7 h-7" />
          </div>
          <TriLingualHeading
            pt={Slide.title.pt}
            en={Slide.title.en}
            es={Slide.title.es}
            className="text-lg text-center"
          />
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          <div data-testid="onboarding-body">
            <TriLingualBody pt={Slide.body.pt} en={Slide.body.en} es={Slide.body.es} />
          </div>

          {/* Dots */}
          <div className="flex items-center justify-center gap-1.5 mt-5">
            {slides.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === step ? 'w-6 bg-demarre-700' : 'w-1.5 bg-gray-300'
                }`}
              />
            ))}
          </div>

          {/* Actions — tri-lingual labels via slashes so ANY user knows what to click. */}
          <div className="flex items-center justify-between gap-2 mt-5">
            <button
              onClick={close}
              className="text-[11px] text-gray-500 hover:text-gray-700 underline-offset-2 hover:underline leading-tight text-left"
              data-testid="onboarding-skip"
            >
              Pular · Skip · Omitir
            </button>
            <div className="flex items-center gap-1.5">
              {step > 0 && (
                <button
                  onClick={() => setStep((s) => s - 1)}
                  className="flex items-center gap-1 px-2.5 py-2 text-[11px] text-gray-600 hover:text-demarre-700 rounded-full leading-tight"
                  data-testid="onboarding-prev"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                  Voltar · Back · Atrás
                </button>
              )}
              <button
                onClick={() => (isLast ? close() : setStep((s) => s + 1))}
                className="flex items-center gap-1 px-3.5 py-2 text-[12px] font-semibold bg-demarre-700 hover:bg-demarre-800 text-white rounded-full shadow leading-tight"
                data-testid={isLast ? 'onboarding-finish' : 'onboarding-next'}
              >
                {isLast ? 'Começar · Start · Comenzar' : 'Próximo · Next · Siguiente'}
                {!isLast && <ChevronRight className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const resetClientOnboarding = () => {
  try { localStorage.removeItem(LS_KEY); } catch { /* ignore */ }
};

export default ClientOnboarding;
